import React from 'react'
import { deriveRowStatus, isDIR, isMOT, resolvePrevType, TYPE_ORDER } from '../targetingUtils'
import targetingDefaultPerTargetLimits from '../../../lookup/admin/targeting/targetingDefaultPerTargetLimits.json'
import states from '../../../lookup/states.json'
import degrees from '../../../lookup/degrees.json'
import styles from '../css/SearchBarV2.module.css'

const STATUS_OPTS = ['added', 'dropped', 'promoted', 'demoted', 'unchanged']
const STATUS_LABELS = {
    added: 'Newly Added',
    dropped: 'Dropped',
    promoted: 'Promoted',
    demoted: 'Demoted',
    unchanged: 'Unchanged',
}

const DEGREE_OPTS = ['MD', 'PA', 'NP', 'APRN', 'DO', 'RN', 'PHAR']

export const EMPTY_FILTERS = {
    FIRST_NAME: '',
    LAST_NAME: '',
    DEGREE: '',
    CITY: '',
    STATE: '',
    PREV_TARGET_TYPE: '',
    CURRENT_TARGET_VALUE: '',
    Q2: '',
    STATUS: '',
    CHANGED_ONLY: false,
}

// Fields updated by target-type actions — excluded from the call-count comparison below
const TARGET_KEYS = new Set(['TARGET_TYPE', 'CHANGED_TARGET_VALUE', 'REASON', 'NOTES', 'CUSTOM_COLUMNS'])

export function applyFilters(rows, filters, recentlyTouched, baselineMap, prevCol = 'PREV_TARGET_TYPE', prevFallbackCol = null) {
    const f = filters
    return rows.filter(row => {
        if (f.CHANGED_ONLY) {
            // 1. Target type change: rep set a Q2 different from the proposed (persists through
            //    reload via CHANGED_TARGET_VALUE in DB, or TARGET_TYPE for in-session overrides)
            const effectiveFinal = row.TARGET_TYPE || row.CHANGED_TARGET_VALUE || ''
            const hasTargetChange = !!effectiveFinal && effectiveFinal !== row.CURRENT_TARGET_VALUE
            // 2. Session touch: any row updated via onRowUpdate this session
            const touchedThisSession = recentlyTouched?.has(row.PROF_ID)
            // 3. Custom column change: compare live row against the session-start baseline.
            //    baseline strips only TARGET_TYPE and CHANGED_TARGET_VALUE so any other diff
            //    here is a user-made column edit (e.g. planned calls) within this session.
            const baselineRow = baselineMap?.get(row.PROF_ID)
            const hasColumnChange = baselineRow
                ? Object.keys(row).some(
                    k => !TARGET_KEYS.has(k) &&
                    String(row[k] ?? '') !== String(baselineRow[k] ?? '')
                  )
                : false
            // 4. Planned call change: persisted cross-session — INITIAL vs FINAL from the call changes table
            const hasCallChange = row.INITIAL_CALL_COUNT != null
                && Number(row.FINAL_CALL_COUNT) !== Number(row.INITIAL_CALL_COUNT)
            if (!hasTargetChange && !touchedThisSession && !hasColumnChange && !hasCallChange) return false
        }
        if (f.FIRST_NAME && !row.FIRST_NAME?.toLowerCase().includes(f.FIRST_NAME.toLowerCase())) return false
        if (f.LAST_NAME  && !row.LAST_NAME?.toLowerCase().includes(f.LAST_NAME.toLowerCase()))  return false
        if (f.DEGREE     && row.DEGREE !== f.DEGREE)                                             return false
        if (f.CITY       && !row.CITY?.toLowerCase().includes(f.CITY.toLowerCase()))             return false
        if (f.STATE             && row.STATE !== f.STATE)                                        return false
        if (f.PREV_TARGET_TYPE  && (resolvePrevType(row, prevCol, prevFallbackCol) || 'NT') !== f.PREV_TARGET_TYPE)         return false
        if (f.CURRENT_TARGET_VALUE && (row.CURRENT_TARGET_VALUE || 'NT') !== f.CURRENT_TARGET_VALUE) return false
        if (f.Q2) {
            if (f.Q2 === 'DIR') {
                if (!isDIR(row)) return false
            } else if (f.Q2 === 'MOT') {
                if (!isMOT(row)) return false
            } else {
                const q2 = row.TARGET_TYPE || row.CHANGED_TARGET_VALUE || row.CURRENT_TARGET_VALUE || ''
                if (q2 !== f.Q2) return false
            }
        }
        if (f.STATUS) {
            if (deriveRowStatus(row, prevCol, prevFallbackCol) !== f.STATUS) return false
        }
        return true
    })
}

export default function SearchBarV2({ filters, onChange, team, columnDefs = [], prevCol = 'PREV_TARGET_TYPE', resultCount, totalCount, allowDIR = false, allowMOT = false, onResetColumns = null, columnPicker = null, visibleTypes = null }) {
    const teamConfig = targetingDefaultPerTargetLimits.team[team]?.defaultTargetOptions ?? null;
    const targetOpts = visibleTypes
        ? [...visibleTypes, 'NT']
        : teamConfig
            ? [...TYPE_ORDER.filter(t => teamConfig[t]?.isVisible), 'NT']
            : ['A', 'B', 'C', 'NT']
    const isFiltered = Object.values(filters).some(v => v !== '' && v !== false)

    // Use client-customized column titles for the type filter placeholders when available
    const toTitleCase = s => s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    const colTitle = (colId, fallback) => {
        const raw = columnDefs.find(d => d.col === colId)?.title
        return raw ? toTitleCase(raw) : fallback
    }
    const prevLabel     = 'Previous'
    const proposedLabel = colTitle('CURRENT_TARGET_VALUE', 'Proposed')
    const finalLabel    = colTitle('CHANGED_TARGET_VALUE', 'Final')

    const set = (key, value) => onChange({ ...filters, [key]: value })
    const clear = () => onChange(EMPTY_FILTERS)

    return (
        <div className={styles.bar}>
            <label className={styles.checkWrap}>
                <input
                    type="checkbox"
                    checked={!!filters.CHANGED_ONLY}
                    onChange={e => set('CHANGED_ONLY', e.target.checked)}
                />
                <span>Changed only</span>
            </label>
            <div className={styles.fields}>
                <input
                    className={styles.textInput}
                    placeholder="First name"
                    value={filters.FIRST_NAME}
                    onChange={e => set('FIRST_NAME', e.target.value)}
                />
                <input
                    className={styles.textInput}
                    placeholder="Last name"
                    value={filters.LAST_NAME}
                    onChange={e => set('LAST_NAME', e.target.value)}
                />
                <select className={styles.select} value={filters.DEGREE} onChange={e => set('DEGREE', e.target.value)}>
                    <option value="">Degree</option>
                    {DEGREE_OPTS.map(d => (
                        <option key={d} value={d}>{d} — {degrees[d] || d}</option>
                    ))}
                </select>
                <input
                    className={styles.textInput}
                    placeholder="City"
                    value={filters.CITY}
                    onChange={e => set('CITY', e.target.value)}
                />
                <select className={styles.select} value={filters.STATE} onChange={e => set('STATE', e.target.value)}>
                    <option value="">State</option>
                    {Object.entries(states).map(([abbr, name]) => (
                        <option key={abbr} value={abbr}>{name}</option>
                    ))}
                </select>
                <select className={styles.select} value={filters.PREV_TARGET_TYPE} onChange={e => set('PREV_TARGET_TYPE', e.target.value)}>
                    <option value="">{prevLabel}</option>
                    {targetOpts.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className={styles.select} value={filters.CURRENT_TARGET_VALUE} onChange={e => set('CURRENT_TARGET_VALUE', e.target.value)}>
                    <option value="">{proposedLabel}</option>
                    {targetOpts.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className={styles.select} value={filters.Q2} onChange={e => set('Q2', e.target.value)}>
                    <option value="">{finalLabel}</option>
                    {targetOpts.map(t => <option key={t} value={t}>{t}</option>)}
                    {allowDIR && <option value="DIR">DIR</option>}
                    {allowMOT && <option value="MOT">MOT</option>}
                </select>
                <select className={styles.select} value={filters.STATUS} onChange={e => set('STATUS', e.target.value)}>
                    <option value="">Status</option>
                    {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>

            </div>
            <div className={styles.right}>
                {isFiltered && (
                    <span className={styles.count}>
                        {resultCount} of {totalCount}
                    </span>
                )}
                {isFiltered && (
                    <button className={styles.clearBtn} onClick={clear}>Clear filters</button>
                )}
                {onResetColumns && (
                    <button className={styles.clearBtn} onClick={onResetColumns}>Reset columns</button>
                )}
                {columnPicker}
            </div>
        </div>
    )
}
